import { create } from 'zustand'
import type { Transaction } from '@flows.cash/types'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase/client'
import { calculateBalance } from '../utils/calculations'
import { formatDateForSupabase } from '../utils'
import uuid from 'react-native-uuid'
import { transferService, TransferTransaction, CreateTransferRequest, UpdateTransferRequest } from '../services/transferService'
import { transactionService } from '../services/transactionService'

// Helper function to apply all filters to a query
const applyFilters = (query: any, filters: any) => {
  // Apply date filters
  if (filters.startDate) {
    const formattedStartDate = formatDateForSupabase(filters.startDate);
    query = query.gte('date', formattedStartDate);
  }
  
  if (filters.endDate) {
    const formattedEndDate = formatDateForSupabase(filters.endDate);
    query = query.lte('date', formattedEndDate);
  }
  
  // Apply array-based filters
  const arrayFilters = [
    { key: 'categories', column: 'category_id' },
    { key: 'accounts', column: 'account_id' },
    { key: 'types', column: 'type' },
    { key: 'payees', column: 'payee_id' }
  ];
  
  arrayFilters.forEach(({ key, column }) => {
    if (filters[key] && Array.isArray(filters[key]) && filters[key].length > 0) {
      query = query.in(column, filters[key]);
    }
  });
  
  // Apply null filters for categories and payees
  if (filters.categoriesIsNull) {
    query = query.is('category_id', null);
  }
  
  if (filters.payeesIsNull) {
    // For payees, simply check for transactions where payee_id is null
    // This is the most reliable indicator of "no payee" since payee_name is often derived
    query = query.is('payee_id', null);
  }
  
  // Apply tags filter - special handling for JSON array field
  if (filters.tags && Array.isArray(filters.tags) && filters.tags.length > 0) {
    // Use PostgreSQL's overlaps operator to check if the tags array has any common elements
    // with the filter tags array. This is equivalent to checking if any tag in the filter
    // exists in the transaction's tags array
    query = query.overlaps('tags', filters.tags);
  }
  
  // Apply search filter
  if (filters.search && filters.search.trim().length > 0) {
    query = applySearchFilter(query, filters.search.trim());
  }
  
  return query;
};

// Helper function to safely escape search terms for ILIKE queries
const escapeSearchTerm = (term: string): string => {
  // Escape special PostgreSQL LIKE characters: % _ \
  return term.replace(/[%_\\]/g, '\\$&');
};

// Helper function to apply search filters securely
const applySearchFilter = (query: any, searchTerm: string) => {
  // Minimum search length for performance
  if (searchTerm.length < 2) {
    console.warn('Search term too short, skipping search filter');
    return query;
  }
  
  const escapedTerm = escapeSearchTerm(searchTerm);
  
  // Use textSearch for better performance if available, otherwise fall back to ilike
  return query.or(
    `description.ilike.%${escapedTerm}%,` +
    `notes.ilike.%${escapedTerm}%,` +
    `payee_name.ilike.%${escapedTerm}%`
  );
};

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

// A helper function to safely get account names without direct store imports
const getAccountNameById = async (accountId: string): Promise<string> => {
  try {
    // Fetch directly from Supabase instead of depending on the accounts store
    const { data, error } = await supabase
      .from('accounts')
      .select('name')
      .eq('id', accountId)
      .single();
      
    if (error) throw error;
    return data?.name || 'Unknown Account';
  } catch (error) {
    console.error('Error fetching account name:', error);
    return 'Unknown Account';
  }
};

// Helper function to transform attachment data from Supabase to PhotoAttachment format
const transformAttachments = async (attachments: any[]): Promise<any[]> => {
  if (!Array.isArray(attachments)) return [];
  
  const transformedAttachments = await Promise.all(
    attachments.map(async (attachment: any) => {
      try {
        // Create a signed URL for private storage access (24 hour expiry)
        const { data: signedUrlData, error } = await supabase.storage
          .from('attachments')
          .createSignedUrl(attachment.file_path, 60 * 60 * 24); // 24 hours
        
        let fileUrl = '';
        if (error) {
          console.error('Error creating signed URL:', error);
          // Fallback to public URL if signed URL fails
          const { data: urlData } = supabase.storage
            .from('attachments')
            .getPublicUrl(attachment.file_path);
          fileUrl = urlData.publicUrl || '';
        } else {
          fileUrl = signedUrlData.signedUrl || '';
        }
        
        return {
          id: attachment.id,
          name: attachment.filename,
          fileName: attachment.filename,
          fileUrl: fileUrl,
          url: fileUrl, // For compatibility
          fileType: attachment.file_type || 'application/octet-stream',
          type: attachment.file_type || 'application/octet-stream', // Required by interface
          size: attachment.file_size || 0,
          uploadedAt: attachment.created_at,
          createdAt: attachment.created_at,
          updatedAt: attachment.updated_at,
          userId: '' // We don't have this in the attachment record, but it's required
        };
      } catch (error) {
        console.error('Error transforming attachment:', error);
        // Return a basic attachment object even if URL generation fails
        return {
          id: attachment.id,
          name: attachment.filename,
          fileName: attachment.filename,
          fileUrl: '',
          url: '',
          fileType: attachment.file_type || 'application/octet-stream',
          type: attachment.file_type || 'application/octet-stream',
          size: attachment.file_size || 0,
          uploadedAt: attachment.created_at,
          createdAt: attachment.created_at,
          updatedAt: attachment.updated_at,
          userId: ''
        };
      }
    })
  );
  
  return transformedAttachments;
};

// Type for transaction with optional ID
interface TransactionInput extends Omit<Transaction, 'id' | 'user_id' | 'created_at' | 'updated_at'> {
  id?: string;
  attachments?: any[];
  // Add additional fields used in transfer transactions
  fromAccountName?: string;
  toAccountName?: string;
  to_account_currency?: string;
}

// Type for transaction filters
interface TransactionFilters {
  search?: string;
  categories?: string[];
  accounts?: string[];
  types?: ('income' | 'expense' | 'transfer')[];
  payees?: string[];
  tags?: string[];
  startDate?: Date | string;
  endDate?: Date | string;
  // Support for filtering transactions with null values
  categoriesIsNull?: boolean;
  payeesIsNull?: boolean;
}

// Type for fetch options
interface FetchOptions {
  limit?: number;
  useCache?: boolean;
  reset?: boolean;
  filters?: TransactionFilters;
}

// Type for pagination options
interface PaginationOptions {
  limit?: number;
  filters?: TransactionFilters;
  cursor?: string;
}

interface TransactionState {
  transactions: Transaction[]
  isLoading: boolean
  error: string | null
  totalCount: number // Add total count from database
  needsRefresh: boolean // Flag to indicate when data needs refreshing
  pagination: {
    isLoading: boolean
    hasMore: boolean
    page: number
    limit: number
    cursor?: string // Add cursor support for date-based pagination
  }
  // Add search cache
  searchCache: Map<string, { results: Transaction[], timestamp: number }>
  fetchTransactions: (userId: string, options?: FetchOptions) => Promise<void>
  fetchNextPage: (userId: string, options?: PaginationOptions) => Promise<Transaction[] | undefined>
  addTransaction: (userId: string, transaction: TransactionInput) => Promise<string | undefined>
  updateTransaction: (userId: string, id: string, transaction: Partial<Transaction>) => Promise<void>
  deleteTransaction: (userId: string, id: string) => Promise<Transaction>
  bulkDeleteTransactions: (userId: string, transactionIds: string[], onProgress?: (current: number, total: number, errors: string[]) => void) => Promise<{
    deletedIds: string[];
    failedIds: string[];
    errors: string[];
  }>
  clearTransactions: () => void
  clearSearchCache: () => void
  markNeedsRefresh: () => void
  clearNeedsRefresh: () => void
  // Enhanced transfer methods
  createTransfer: (userId: string, request: CreateTransferRequest) => Promise<TransferTransaction>
  updateTransfer: (userId: string, transferId: string, updates: UpdateTransferRequest) => Promise<TransferTransaction>
  getTransferById: (userId: string, transferId: string) => Promise<TransferTransaction | null>
  getTransferByTransactionId: (userId: string, transactionId: string) => Promise<TransferTransaction | null>
  deleteTransfer: (userId: string, transferId: string) => Promise<void>
  splitTransfer: (userId: string, transferId: string) => Promise<{ incomeTransaction: Transaction; expenseTransaction: Transaction }>
  isTransferTransaction: (userId: string, transactionId: string) => Promise<boolean>
  convertToTransfer: (userId: string, sourceTransaction: Transaction, targetTransaction: Transaction) => Promise<TransferTransaction>
}

export const useTransactionStore = create<TransactionState>()(
  persist(
    (set, get) => ({
      transactions: [],
      isLoading: false,
      error: null,
      totalCount: 0,
      needsRefresh: false,
      pagination: {
        isLoading: false,
        hasMore: true,
        page: 1,
        limit: 200,
      },
      searchCache: new Map<string, { results: Transaction[], timestamp: number }>(),

      markNeedsRefresh: () => {
        set({ needsRefresh: true });
      },

      clearNeedsRefresh: () => {
        set({ needsRefresh: false });
      },

      fetchTransactions: async (userId: string, options = {}) => {
        const { limit = 200, useCache = true, reset = true, filters = {} } = options;
        
        // Check cache for search results (only for search queries)
        if (filters.search && useCache) {
          const cacheKey = JSON.stringify({ userId, filters, limit });
          let searchCache = get().searchCache;
          
          // Ensure the cache is always a Map (persistence can turn it into a plain object)
          if (!searchCache || typeof searchCache.get !== 'function') {
            searchCache = new Map<string, { results: Transaction[], timestamp: number }>();
            set({ searchCache });
          }
          
          const cached = searchCache.get(cacheKey);
          const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
          
          if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
            console.log('Using cached search results for:', filters.search);
            set({ 
              transactions: cached.results, 
              isLoading: false,
              pagination: {
                ...get().pagination,
                hasMore: cached.results.length >= limit,
                page: 1,
                limit,
                cursor: cached.results.length > 0 ? cached.results[cached.results.length - 1].date : undefined
              }
            });
            return;
          }
        }
        
        // Reset state if requested
        if (reset) {
          set({ 
            isLoading: true, 
            error: null,
            transactions: useCache ? get().transactions : [] 
          })
        } else {
          set({ isLoading: true, error: null })
        }
        
        try {
          // First, get the total count with the same filters
          let countQuery = supabase
            .from('transactions')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId);
          
          // Apply the same filters to count query
          countQuery = applyFilters(countQuery, filters);
          
          const { count, error: countError } = await countQuery;
          
          if (countError) throw countError;
          
          // Then get the actual data
          let query = supabase
            .from('transactions')
            .select(`
              *,
              attachments!transaction_id (
                id,
                filename,
                file_path,
                file_size,
                file_type,
                created_at,
                updated_at
              )
            `)
            .eq('user_id', userId)
            .order('date', { ascending: false })
            .limit(limit);
          
          // Apply filters with proper date formatting
          query = applyFilters(query, filters);

          const { data: transactions, error } = await query;

          if (error) throw error
          
          // Transform transactions to include properly formatted attachments
          const transformedTransactions = Array.isArray(transactions) ? await Promise.all(
            transactions.map(async (transaction) => ({
              ...transaction,
              attachments: await transformAttachments(transaction.attachments || [])
            }))
          ) : [];
          
          // Get the cursor from the last transaction for pagination
          const cursor = transformedTransactions.length > 0
            ? transformedTransactions[transformedTransactions.length - 1].date
            : undefined;
          
          // Cache search results for future use
          if (filters.search && transformedTransactions.length > 0) {
            const cacheKey = JSON.stringify({ userId, filters, limit });
            let currentCache = get().searchCache;
            
            // Ensure the cache is always a Map (persistence can turn it into a plain object)
            if (!currentCache || typeof currentCache.set !== 'function') {
              currentCache = new Map<string, { results: Transaction[], timestamp: number }>();
            }
            
            currentCache.set(cacheKey, {
              results: transformedTransactions,
              timestamp: Date.now()
            });
            
            // Limit cache size to prevent memory issues (keep last 50 searches)
            if (currentCache.size > 50) {
              const firstKey = currentCache.keys().next().value;
              if (firstKey) {
                currentCache.delete(firstKey);
              }
            }
            
            set({ searchCache: currentCache });
          }
          
          set({ 
            transactions: transformedTransactions, 
            isLoading: false,
            needsRefresh: false,
            totalCount: count || 0,
            pagination: {
              ...get().pagination,
              hasMore: (transformedTransactions?.length || 0) >= limit,
              page: 1,
              limit,
              cursor // Initialize the cursor for pagination
            }
          })
        } catch (error) {
          console.error('Error fetching transactions:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch transactions',
            isLoading: false,
          })
        }
      },

      fetchNextPage: async (userId: string, options = {}) => {
        const { limit = 200, filters = {}, cursor } = options;
        const state = get();
        
        // If already loading or no more pages, don't fetch
        if (state.pagination.isLoading || !state.pagination.hasMore) {
          console.log('FetchNextPage skipped:', {
            isLoading: state.pagination.isLoading,
            hasMore: state.pagination.hasMore
          });
          return;
        }
        
        console.log('FetchNextPage starting:', {
          userId,
          limit,
          cursor,
          currentTransactionCount: state.transactions?.length || 0
        });
        
        set({ 
          pagination: {
            ...state.pagination,
            isLoading: true
          }
        });
        
        try {
          // Use the last transaction date as cursor instead of offset-based pagination
          let query = supabase
            .from('transactions')
            .select(`
              *,
              attachments!transaction_id (
                id,
                filename,
                file_path,
                file_size,
                file_type,
                created_at,
                updated_at
              )
            `)
            .eq('user_id', userId)
            .order('date', { ascending: false });
            
          // If we have a cursor, use it (typically the date of the last item)
          if (cursor) {
            query = query.lt('date', cursor);
          }
          
          // Add the limit
          query = query.limit(limit);
          
          // Apply filters with proper date formatting
          query = applyFilters(query, filters);

          const { data, error } = await query;
          
          if (error) {
            throw error;
          }
          
          // Transform transactions to include properly formatted attachments
          const transformedNewTransactions = Array.isArray(data) ? await Promise.all(
            data.map(async (transaction) => ({
              ...transaction,
              attachments: await transformAttachments(transaction.attachments || [])
            }))
          ) : [];
          
          console.log('FetchNextPage received:', {
            newTransactions: transformedNewTransactions.length,
            limit,
            hasMoreData: transformedNewTransactions.length === limit
          });
          
          // Make sure we have valid arrays
          const safeCurrentTransactions = Array.isArray(state.transactions) ? state.transactions : [];
          
          // Determine if there are more transactions to load
          // If we got fewer transactions than requested, there are no more
          const hasMore = transformedNewTransactions.length === limit;
          
          // Get the new cursor if there are transactions
          const newCursor = transformedNewTransactions.length > 0 
            ? transformedNewTransactions[transformedNewTransactions.length - 1].date 
            : cursor;
          
          console.log('FetchNextPage updating state:', {
            addingTransactions: transformedNewTransactions.length,
            newTotal: safeCurrentTransactions.length + transformedNewTransactions.length,
            hasMore,
            newCursor
          });
          
          // Only update state if we actually have transactions to add or need to update hasMore
          const newTransactionsList = [...safeCurrentTransactions, ...transformedNewTransactions];
          
          set({
            transactions: newTransactionsList,
            pagination: {
              ...state.pagination,
              isLoading: false,
              page: state.pagination.page + 1,
              cursor: newCursor,
              hasMore
            }
          });
          
          // Update account balances if needed
          const affectedAccountIds = new Set<string>();
          transformedNewTransactions.forEach(tx => {
            if (tx.account_id) affectedAccountIds.add(tx.account_id);
            if (tx.to_account_id) affectedAccountIds.add(tx.to_account_id);
          });
          
          if (affectedAccountIds.size > 0) {
            updateAccountBalances(Array.from(affectedAccountIds));
          }
          
          return transformedNewTransactions;
        } catch (error) {
          console.error('Error fetching transactions with cursor pagination:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch next page',
            pagination: {
              ...state.pagination,
              isLoading: false,
            },
          });
          throw error;
        }
      },

      addTransaction: async (userId: string, transaction: TransactionInput) => {
        set({ isLoading: true, error: null })
        try {
          // Ensure we have valid IDs
          if (!userId || userId.trim() === '') {
            throw new Error('Invalid or empty user ID')
          }

          // Handle the case where transaction might have an id property that is empty
          let transactionWithId: TransactionInput = { ...transaction }
          
          // Extra safeguards for ID
          if (!transactionWithId.id || typeof transactionWithId.id !== 'string' || transactionWithId.id.trim() === '') {
            // Generate a new UUID if missing or invalid
            transactionWithId.id = uuid.v4().toString();
          }

          // Check if this is a transfer transaction
          if (transactionWithId.type === 'transfer' && transactionWithId.to_account_id) {
            // For transfers, create two linked transactions
            const outgoingId = uuid.v4().toString()
            const incomingId = uuid.v4().toString()
            const now = new Date().toISOString()

            // Get account names using our helper function instead of directly accessing the store
            const fromAccountName = (transactionWithId as any).fromAccountName || 
              await getAccountNameById(transactionWithId.account_id);
            const toAccountName = (transactionWithId as any).toAccountName || 
              (transactionWithId.to_account_id ? await getAccountNameById(transactionWithId.to_account_id) : 'Unknown Account');
            const fromCurrency = transactionWithId.currency || 'USD'
            
            // Get the destination account's currency properly
            let toCurrency = 'USD'; // default fallback
            if (transactionWithId.to_account_currency) {
              toCurrency = transactionWithId.to_account_currency;
            } else if ((transactionWithId as any).toCurrency) {
              toCurrency = (transactionWithId as any).toCurrency;
            } else if (transactionWithId.to_account_id) {
              // Look up the destination account's currency from the accounts store
              try {
                const { useAccountsStore } = await import('@flows.cash/core');
                const accountsState = useAccountsStore.getState();
                const toAccount = accountsState.accounts?.find(a => a.id === transactionWithId.to_account_id);
                if (toAccount?.currency) {
                  toCurrency = toAccount.currency;
                }
              } catch (error) {
                console.warn('Could not look up destination account currency:', error);
                // Keep the default fallback
              }
            }

            // Common fields for transfer details
            const transferDetails = {
              isTransfer: true,
              fromAccountId: transactionWithId.account_id,
              toAccountId: transactionWithId.to_account_id,
              fromAccountName,
              toAccountName,
              fromCurrencyCode: fromCurrency,
              toCurrencyCode: toCurrency,
              exchangeRate: transactionWithId.exchange_rate || 1,
            }

            // Calculate the converted amount
            const transferAmount = Math.abs(transactionWithId.amount)
            const exchangeRate = transactionWithId.exchange_rate || 1
            const convertedAmount = transferAmount * exchangeRate

            // Outgoing transaction (expense)
            const outgoingTransaction: Transaction = {
              id: outgoingId,
              user_id: userId,
              account_id: transactionWithId.account_id,
              category_id: transactionWithId.category_id,
              payee_id: undefined,
              payee_name: `Transfer to ${toAccountName}`,
              type: 'transfer',
              amount: -transferAmount,
              currency: fromCurrency,
              date: transactionWithId.date,
              description: transactionWithId.description || '',
              notes: transactionWithId.notes || '',
              status: transactionWithId.status || 'completed',
              created_at: now,
              updated_at: now,
              metadata: {
                ...transactionWithId.metadata,
                ...transferDetails,
                linkedTransactionId: incomingId,
                isOutgoing: true
              }
            }

            // Incoming transaction (income)
            const incomingTransaction: Transaction = {
              id: incomingId,
              user_id: userId,
              account_id: transactionWithId.to_account_id,
              category_id: transactionWithId.category_id,
              payee_id: undefined,
              payee_name: `Transfer from ${fromAccountName}`,
              type: 'transfer',
              amount: convertedAmount,
              currency: toCurrency,
              date: transactionWithId.date,
              description: transactionWithId.description || '',
              notes: transactionWithId.notes || '',
              status: transactionWithId.status || 'completed',
              created_at: now,
              updated_at: now,
              metadata: {
                ...transactionWithId.metadata,
                ...transferDetails,
                linkedTransactionId: outgoingId,
                isIncoming: true
              }
            }

            // Insert both transactions
            const { error: outgoingError } = await supabase
              .from('transactions')
              .insert([outgoingTransaction])

            if (outgoingError) throw outgoingError

            const { error: incomingError } = await supabase
              .from('transactions')
              .insert([incomingTransaction])

            if (incomingError) {
              // If inserting the incoming transaction fails, clean up the outgoing one
              await supabase
                .from('transactions')
                .delete()
                .eq('id', outgoingId)
              throw incomingError
            }

            // Update local state
            set((state) => ({
              transactions: Array.isArray(state.transactions) ? [incomingTransaction, outgoingTransaction, ...state.transactions] : [incomingTransaction, outgoingTransaction],
              isLoading: false,
            }))

            // Update account balances
            await updateAccountBalances([transactionWithId.account_id, transactionWithId.to_account_id])
            return outgoingId
          } else {
            // Regular transaction (not a transfer)
            const createdAt = new Date().toISOString()
            const updatedAt = new Date().toISOString()
            
            const newTransaction = {
              id: transactionWithId.id,
              user_id: userId,
              account_id: transactionWithId.account_id,
              category_id: transactionWithId.category_id,
              payee_id: transactionWithId.payee_id,
              payee_name: transactionWithId.payee_name,
              type: transactionWithId.type,
              amount: transactionWithId.amount,
              currency: transactionWithId.currency,
              date: transactionWithId.date,
              description: transactionWithId.description || '',
              notes: transactionWithId.notes || '',
              status: transactionWithId.status || 'completed',
              tags: transactionWithId.tags || [],
              metadata: transactionWithId.metadata || {},
              created_at: createdAt,
              updated_at: updatedAt,
            }

            // Final validation check for ID
            if (!newTransaction.id || typeof newTransaction.id !== 'string' || newTransaction.id.trim() === '') {
              // Generate a new UUID as a last resort
              newTransaction.id = uuid.v4().toString();
            }

            const { error } = await supabase
              .from('transactions')
              .insert([newTransaction])

            if (error) throw error

            // Update local state
            set((state) => ({
              transactions: Array.isArray(state.transactions) ? [newTransaction, ...state.transactions] : [newTransaction],
              isLoading: false,
            }))

            // Update account balance
            await updateAccountBalances([transactionWithId.account_id])
            return newTransaction.id
          }
        } catch (error) {
          console.error('Error adding transaction:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to add transaction',
            isLoading: false,
          })
          throw error
        }
      },

      updateTransaction: async (_userId: string, id: string, transaction) => {
        set({ isLoading: true, error: null })
        try {
          // First, fetch the existing transaction
          const { data: existingTransaction, error: fetchError } = await supabase
            .from('transactions')
            .select('*')
            .eq('id', id)
            .single()

          if (fetchError) {
            // Check if this is a "no rows returned" error
            if (fetchError.code === 'PGRST116' || fetchError.message.includes('no rows')) {
              console.error(`Transaction with ID ${id} not found in the database. It may have been deleted.`);
              set({
                error: `Transaction not found: ${id}. It may have been deleted.`,
                isLoading: false,
              });
              throw new Error(`Transaction with ID ${id} not found. It may have been deleted.`);
            }
            
            console.error('Error fetching transaction for update:', fetchError)
            throw fetchError
          }

          if (!existingTransaction) {
            const notFoundError = new Error(`Transaction with ID ${id} not found`);
            console.error(notFoundError);
            set({
              error: notFoundError.message,
              isLoading: false,
            });
            throw notFoundError;
          }

          const updatedTransaction = {
            ...existingTransaction,
            ...transaction,
            amount: transaction.type === 'expense' ? -Math.abs(transaction.amount || existingTransaction.amount) : Math.abs(transaction.amount || existingTransaction.amount),
            updated_at: new Date().toISOString(),
          }

          // Implement retry logic for network issues
          const maxRetries = 3
          let retryCount = 0
          let updateError = null

          while (retryCount < maxRetries) {
            try {
              const { error } = await supabase
                .from('transactions')
                .update(updatedTransaction)
                .eq('id', id)

              if (error) {
                updateError = error
                retryCount++
                
                // If it's likely a network error, wait before retrying
                if (error.message.includes('Failed to fetch') || error.code === 'NETWORK_ERROR') {
                  await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)) // Exponential backoff
                  continue
                } else {
                  // If it's not a network error, don't retry
                  throw error
                }
              } else {
                // Success - break out of retry loop
                updateError = null
                break
              }
            } catch (e) {
              updateError = e
              retryCount++
              
              // Wait before retrying
              if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 1000 * retryCount))
              }
            }
          }

          // If we still have an error after all retries, throw it
          if (updateError) {
            throw updateError
          }

          // Update local state
          set((state) => ({
            transactions: Array.isArray(state.transactions) ? state.transactions.map((t) =>
              t.id === id ? updatedTransaction : t
            ) : [],
            isLoading: false,
          }))

          // Update account balance
          await updateAccountBalances([updatedTransaction.account_id])
        } catch (error) {
          console.error('Error updating transaction:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to update transaction',
            isLoading: false,
          })
          throw error
        }
      },

      deleteTransaction: async (userId: string, id: string) => {
        set({ isLoading: true, error: null })
        try {
          const { data: transaction, error: fetchError } = await supabase
            .from('transactions')
            .select('*')
            .eq('id', id)
            .single()

          if (fetchError) throw fetchError

          // Check if this is a transfer transaction and handle linked transaction deletion
          let linkedTransactions: any[] = []
          
          // New transfer system: Check for transfer_id
          if (transaction.transfer_id) {
            // Get all transactions with the same transfer_id
            const { data: transferTransactions, error: transferError } = await supabase
              .from('transactions')
              .select('*')
              .eq('transfer_id', transaction.transfer_id)
              .eq('user_id', userId)

            if (transferError) throw transferError
            
            if (transferTransactions && transferTransactions.length > 0) {
              linkedTransactions = transferTransactions
              console.log(`Found ${linkedTransactions.length} linked transfer transactions to delete`)
            }
          }
          // Legacy transfer system: Check for metadata.isTransfer
          else if (transaction.metadata?.isTransfer && transaction.metadata?.linkedTransactionId) {
            const { data: legacyLinked, error: legacyError } = await supabase
              .from('transactions')
              .select('*')
              .eq('id', transaction.metadata.linkedTransactionId)
              .eq('user_id', userId)
              .single()

            if (!legacyError && legacyLinked) {
              linkedTransactions = [transaction, legacyLinked]
              console.log('Found legacy linked transfer transaction to delete')
            } else {
              linkedTransactions = [transaction]
            }
          } else {
            linkedTransactions = [transaction]
          }

          // Delete all linked transactions
          const accountIds = new Set<string>()
          for (const txn of linkedTransactions) {
            accountIds.add(txn.account_id)
            
            const { error: deleteError } = await supabase
              .from('transactions')
              .delete()
              .eq('id', txn.id)

            if (deleteError) throw deleteError
            console.log(`Deleted transaction: ${txn.id}`)
          }

          // Update local state - remove all deleted transactions
          const deletedIds = linkedTransactions.map(t => t.id)
          set((state) => ({
            transactions: Array.isArray(state.transactions) 
              ? state.transactions.filter((t) => !deletedIds.includes(t.id)) 
              : [],
            isLoading: false,
          }))

          // Update account balances for all affected accounts
          await updateAccountBalances(Array.from(accountIds))
          
          // Return the original transaction data for notifications/UI feedback
          return transaction
        } catch (error) {
          console.error('Error deleting transaction:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to delete transaction',
            isLoading: false,
          })
          throw error
        }
      },

      clearTransactions: () => {
        set({ 
          transactions: [], 
          totalCount: 0,
          pagination: {
            isLoading: false,
            hasMore: true,
            page: 1,
            limit: 200
          }
        });
      },

      clearSearchCache: () => {
        set({ searchCache: new Map<string, { results: Transaction[], timestamp: number }>() });
      },

      // Enhanced transfer methods using the new transfer service
      createTransfer: async (userId: string, request: CreateTransferRequest) => {
        set({ isLoading: true, error: null });
        try {
          const transfer = await transferService.createTransfer(userId, request);
          
          // Add both transactions to local state
          if (transfer.fromTransaction && transfer.toTransaction) {
            const fromTx = transfer.fromTransaction as Transaction;
            const toTx = transfer.toTransaction as Transaction;
            set((state) => ({
              transactions: Array.isArray(state.transactions) 
                ? [fromTx, toTx, ...state.transactions]
                : [fromTx, toTx],
              isLoading: false,
            }));
          }
          
          // Update account balances
          await updateAccountBalances([request.fromAccountId, request.toAccountId]);
          
          return transfer;
        } catch (error) {
          console.error('Error creating transfer:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to create transfer',
            isLoading: false,
          });
          throw error;
        }
      },

      updateTransfer: async (userId: string, transferId: string, updates: UpdateTransferRequest) => {
        set({ isLoading: true, error: null });
        try {
          const updatedTransfer = await transferService.updateTransfer(userId, transferId, updates);
          
          // Update both transactions in local state
          if (updatedTransfer.fromTransaction && updatedTransfer.toTransaction) {
            const fromTx = updatedTransfer.fromTransaction;
            const toTx = updatedTransfer.toTransaction;
            set((state) => ({
              transactions: Array.isArray(state.transactions) 
                ? state.transactions.map((t) => {
                    if (t.id === fromTx.id) return fromTx;
                    if (t.id === toTx.id) return toTx;
                    return t;
                  })
                : [],
              isLoading: false,
            }));
          }
          
          // Update account balances
          await updateAccountBalances([updatedTransfer.fromAccountId, updatedTransfer.toAccountId]);
          
          return updatedTransfer;
        } catch (error) {
          console.error('Error updating transfer:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update transfer',
            isLoading: false,
          });
          throw error;
        }
      },

      getTransferById: async (userId: string, transferId: string) => {
        try {
          return await transferService.getTransferById(userId, transferId);
        } catch (error) {
          console.error('Error getting transfer:', error);
          return null;
        }
      },

      getTransferByTransactionId: async (userId: string, transactionId: string) => {
        try {
          return await transferService.getTransferByTransactionId(userId, transactionId);
        } catch (error) {
          console.error('Error getting transfer by transaction ID:', error);
          return null;
        }
      },

      deleteTransfer: async (userId: string, transferId: string) => {
        set({ isLoading: true, error: null });
        try {
          // Get transfer details before deletion for account balance updates
          const transfer = await transferService.getTransferById(userId, transferId);
          
          if (transfer) {
            await transferService.deleteTransfer(userId, transferId);
            
            // Remove both transactions from local state immediately
            set((state) => ({
              transactions: Array.isArray(state.transactions) 
                ? state.transactions.filter((t) => (t as any).transfer_id !== transferId)
                : [],
              isLoading: false,
              // Clear search cache to ensure fresh data on next search
              searchCache: new Map<string, { results: Transaction[], timestamp: number }>()
            }));
            
            // Update account balances
            await updateAccountBalances([transfer.fromAccountId, transfer.toAccountId]);
          }
        } catch (error) {
          console.error('Error deleting transfer:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to delete transfer',
            isLoading: false,
          });
          throw error;
        }
      },

      splitTransfer: async (userId: string, transferId: string) => {
        set({ isLoading: true, error: null });
        try {
          // Get transfer details before splitting for account balance updates
          const transfer = await transferService.getTransferById(userId, transferId);
          
          if (transfer) {
            const result = await transferService.splitTransfer(userId, transferId);
            
            // Clear all local state to force a complete refresh
            set({
              transactions: [],
              isLoading: false,
              needsRefresh: false,
              totalCount: 0,
              pagination: {
                isLoading: false,
                hasMore: true,
                page: 1,
                limit: 200
              },
              searchCache: new Map<string, { results: Transaction[], timestamp: number }>()
            });
            
            // Update account balances
            await updateAccountBalances([transfer.fromAccountId, transfer.toAccountId]);
            
            return result;
          } else {
            throw new Error('Transfer not found');
          }
        } catch (error) {
          console.error('Error splitting transfer:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to split transfer',
            isLoading: false,
          });
          throw error;
        }
      },

      isTransferTransaction: async (userId: string, transactionId: string) => {
        try {
          return await transferService.isTransferTransaction(userId, transactionId);
        } catch (error) {
          console.error('Error checking if transaction is transfer:', error);
          return false;
        }
      },

      convertToTransfer: async (userId: string, sourceTransaction: Transaction, targetTransaction: Transaction) => {
        set({ isLoading: true, error: null });
        try {
          const transfer = await transferService.convertToTransfer(userId, sourceTransaction, targetTransaction);
          
          // Update both transactions in local state
          if (transfer.fromTransaction && transfer.toTransaction) {
            const fromTx = transfer.fromTransaction;
            const toTx = transfer.toTransaction;
            set((state) => ({
              transactions: Array.isArray(state.transactions) 
                ? state.transactions.map((t) => {
                    if (t.id === fromTx.id) return fromTx;
                    if (t.id === toTx.id) return toTx;
                    return t;
                  })
                : [],
              isLoading: false,
            }));
          }
          
          // Update account balances
          await updateAccountBalances([transfer.fromAccountId, transfer.toAccountId]);
          
          return transfer;
        } catch (error) {
          console.error('Error converting to transfer:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to convert to transfer',
            isLoading: false,
          });
          throw error;
        }
      },

      bulkDeleteTransactions: async (userId: string, transactionIds: string[], onProgress?: (current: number, total: number, errors: string[]) => void) => {
        set({ isLoading: true, error: null });
        try {
          const result = await transactionService.bulkDeleteTransactions(userId, transactionIds, onProgress);
          
          // Remove deleted transactions from local state
          set((state) => ({
            transactions: Array.isArray(state.transactions) 
              ? state.transactions.filter((t) => !result.deletedIds.includes(t.id))
              : [],
            isLoading: false,
          }));

          // Update account balances if we have any successful deletions
          if (result.deletedIds.length > 0) {
            console.log(`Bulk delete completed: ${result.deletedIds.length} deleted, ${result.failedIds.length} failed`);
          }

          return result;
        } catch (error) {
          console.error('Error bulk deleting transactions:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to bulk delete transactions',
            isLoading: false,
          });
          throw error;
        }
      },
    }),
    {
      name: 'transactions-storage',
      storage: createJSONStorage(() => getStorage()),
      // Add state transformation to handle Map serialization
      partialize: (state) => ({
        ...state,
        // Convert Map to array for serialization
        searchCache: Array.from(state.searchCache.entries())
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Convert array back to Map after deserialization
          if (Array.isArray(state.searchCache)) {
            state.searchCache = new Map(state.searchCache);
          } else {
            // Fallback to empty Map if cache is corrupted
            state.searchCache = new Map<string, { results: Transaction[], timestamp: number }>();
          }
        }
      }
    }
  )
)

// Helper function to update account balances
async function updateAccountBalances(accountIds: string[]) {
  try {
    // Get all transactions for the affected accounts
    const { data: transactions, error: fetchError } = await supabase
      .from('transactions')
      .select('*')
      .in('account_id', accountIds)

    if (fetchError) throw fetchError

    // Get account details
    const { data: accounts, error: accountsError } = await supabase
      .from('accounts')
      .select('*')
      .in('id', accountIds)

    if (accountsError) throw accountsError

    // Update each account's balance
    for (const account of accounts) {
      const accountTransactions = transactions.filter(t => t.account_id === account.id)
      const balance = calculateBalance(accountTransactions, account.type)

      const { error: updateError } = await supabase
        .from('accounts')
        .update({ balance, updated_at: new Date().toISOString() })
        .eq('id', account.id)

      if (updateError) throw updateError
    }
  } catch (error) {
    console.error('Error updating account balances:', error)
    throw error
  }
}
